import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GMB } from "../../services/gmbApiConfig";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "react-toastify";

const validatePostData = ({ accountId, locationId, summary, event, media }) => {
  const missingFields = [];

  if (!accountId) missingFields.push("accountId");
  if (!locationId) missingFields.push("locationId");
  if (!summary) missingFields.push("summary");
  if (!event) missingFields.push("event");

  if (!media || !Array.isArray(media) || media.length === 0) {
    missingFields.push("media");
  }

  if (!event?.title) missingFields.push("event.title");
  if (!event?.schedule) {
    missingFields.push("event.schedule");
  } else {
    const { schedule } = event;

    // Check startDate fields
    if (!schedule.startDate) {
      missingFields.push("event.schedule.startDate");
    } else {
      if (schedule.startDate.year == null)
        missingFields.push("event.schedule.startDate.year");
      if (schedule.startDate.month == null)
        missingFields.push("event.schedule.startDate.month");
      if (schedule.startDate.day == null)
        missingFields.push("event.schedule.startDate.day");
    }

    // Check endDate fields
    if (!schedule.endDate) {
      missingFields.push("event.schedule.endDate");
    } else {
      if (schedule.endDate.year == null)
        missingFields.push("event.schedule.endDate.year");
      if (schedule.endDate.month == null)
        missingFields.push("event.schedule.endDate.month");
      if (schedule.endDate.day == null)
        missingFields.push("event.schedule.endDate.day");
    }

    // Check startTime fields
    if (!schedule.startTime) {
      missingFields.push("event.schedule.startTime");
    } else {
      if (schedule.startTime.hours == null)
        missingFields.push("event.schedule.startTime.hours");
      if (schedule.startTime.min == null)
        missingFields.push("event.schedule.startTime.min");
      if (schedule.startTime.sec == null)
        missingFields.push("event.schedule.startTime.sec");
    }

    // Check endTime fields
    if (!schedule.endTime) {
      missingFields.push("event.schedule.endTime");
    } else {
      if (schedule.endTime.hours == null)
        missingFields.push("event.schedule.endTime.hours");
      if (schedule.endTime.min == null)
        missingFields.push("event.schedule.endTime.min");
      if (schedule.endTime.sec == null)
        missingFields.push("event.schedule.endTime.sec");
    }
  }

  if (missingFields.length > 0) {
    console.error("Missing required fields", { missingFields });
    return Promise.reject(
      `Missing required fields: ${missingFields?.join(", ")}`
    );
  }
};

export const fetchAllPosts = createAsyncThunk(
  "gmb/fetchAllPosts",
  async ({ account_id, location_id }) => {
    const response = await axios.post(
      `${GMB.ENDPOINTS.POSTS.GET_ALL_POSTS}`,
      { account_id, location_id },
      {
        headers: {
          Authorization: Cookies.get("ssp_sso_token"),
          "Content-Type": "application/json",
        },
      }
    );
    const data = await response.data.posts;
    const allPostUrl = await data?.map((item) => ({
      id: item.name?.split("/").pop(),
      url:
            item.media && item.media.length > 0
              ? item.media[0].googleUrl
              : "https://placehold.co/600x400?text=No Image",
      state: item.state,
      topicType: item.topicType,
    }));
    return allPostUrl;
  }
);

export const addUpdatePost = createAsyncThunk(
  "gmb/AddUpdatePost",
  async (
    { accountId, locationId, summary, event, media, topicType, s3Key,callToAction,offer },
    { rejectWithValue }
  ) => {
    let payload = {
      account_id: accountId,
      location_id: locationId,
      languageCode: "en",
      summary: summary,
      event,
      media,
      topicType: topicType,
      s3_key: s3Key,
    };
    if (callToAction) {
     payload={
      ...payload,
      callToAction:callToAction,
     }
    }

    if(offer){
      payload={
      ...payload,
      offer:offer,
     }
    }

    
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.POSTS.ADD_UPDATE}`,
        payload,
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) {
        toast.error(response.data.error_message);
        return rejectWithValue(response.data.error_message);
      }
      toast.success("Post added successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response.data.error);
      return rejectWithValue("Failed to add post");
    }
  }
);

export const deletePosts = createAsyncThunk(
  "gmb/DeletePosts",
  async ({ accountId, locationId, postId }, { rejectWithValue }) => {
    try {
      if (!accountId || !locationId || !postId) {
        console.error("accountId, locationId or postId is not provided", {
          accountId,
          locationId,
          postId,
        });
        throw new Error(" accountId, locationId or postId is not provided");
      }

      const response = await axios.post(
        `${GMB.ENDPOINTS.POSTS.DELETE_POSTS}`,
        { account_id: accountId, location_id: locationId, post_id: postId },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Post deleted successfully");
      }
      return postId;
    } catch (error) {
      toast.error(error.response.data.error_message);
      return rejectWithValue("Failed to delete post");
    }
  }
);

const initialState = {
  allPosts: [],
  loadingStates: {}, // Track loading states for each API call
  error: null,
  selectedPost: [],
};

export const gmbPostSlice = createSlice({
  name: "gmbAllPosts",
  initialState,
  reducers: {
    setAllPosts: (state, action) => {
      state.allPosts = action.payload;
    },
    setSelectedPost: (state, action) => {
      state.selectedPost = action.payload;
    },
    resetPostData: (state) => {
      state.selectedPost = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all posts
      .addCase(fetchAllPosts.pending, (state) => {
        state.loadingStates.fetchAllPosts = true;
      })
      .addCase(fetchAllPosts.fulfilled, (state, action) => {
        state.loadingStates.fetchAllPosts = false;
        state.allPosts = action.payload;
      })
      .addCase(fetchAllPosts.rejected, (state, action) => {
        state.loadingStates.fetchAllPosts = false;
        state.error = action.error.message;
      })
      // Add/Update post
      .addCase(addUpdatePost.pending, (state) => {
        state.loadingStates.addUpdatePost = true;
      })
      .addCase(addUpdatePost.fulfilled, (state, action) => {
        state.loadingStates.addUpdatePost = false;
        const post = {
          id: action.payload.response.name.split("/").pop(),
          url:
            action.payload.response.media && action.payload.response.media.length > 0
              ? action.payload.response.media[0].googleUrl
              : "https://placehold.co/600x400?text=No Image",
          state: action.payload.response.state,
          topicType: action.payload.response.topicType,
        };
        state.allPosts = [...state.allPosts, post];
      })
      .addCase(addUpdatePost.rejected, (state, action) => {
        state.loadingStates.addUpdatePost = false;
        state.error = action.error.message;
      })
      // Delete post
      .addCase(deletePosts.pending, (state) => {
        state.loadingStates.deletePosts = true;
      })
      .addCase(deletePosts.fulfilled, (state, action) => {
        state.loadingStates.deletePosts = false;
        state.allPosts = state.allPosts.filter(
          (post) => post.id !== action.payload
        );
      })
      .addCase(deletePosts.rejected, (state, action) => {
        state.loadingStates.deletePosts = false;
        state.error = action.error.message;
      });
  },
});

// Actions
export const { setAllPosts, setSelectedPost, resetPostData } =
  gmbPostSlice.actions;

// Selectors
export const selectAllPosts = (state) => state.gmbPostSlice.allPosts;
export const selectLoadingState = (state, key) =>
  state.gmbPostSlice.loadingStates[key] || false;
export const selectAllPostsError = (state) => state.gmbPostSlice.error;
export const selectSelectedPosts = (state) => state.gmbPostSlice.selectedPost;

// Reducer
export default gmbPostSlice.reducer;
